import React, { useState, useEffect } from "react";
import {
  Alert,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  List,
  ListItem,
  ListItemText,
  Button,
  Link,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  ListItemAvatar,
  CircularProgress, ListItemButton, DialogTitle, DialogContent, DialogContentText, DialogActions, Dialog
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { StyleTextField, StyledBox, SearchRow, LoadingWrapper } from "./styles";
import { useCustomer } from "context/customer/customerContext";
import {useUI} from "context/ui/uiContext";
import map_animation from "assets/animations/map_location.json";
import Lottie from "react-lottie";
import Transform from 'classes/transform';
import {useIsMobile} from "hooks/useIsMobile";

const states = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"];
/**
 * Delivery component handles the user's interaction for selecting a delivery or drop-off option for a vehicle.
 * It allows users to search for dealers based on their zip code or current location and manage delivery options.
 * Users can choose to drop off the vehicle at a selected dealer or request a pickup from their location.
 * @returns {Element} Returns a JSX element containing the Delivery UI and functionality.
 * @description
 * The Delivery component uses a variety of React hooks to manage state, including:
 * - `loading`: Indicates if the dealer search is in progress.
 * - `details`: Contains the customer details, such as name and address.
 * - `selectedDealer`: The dealer selected by the user for drop-off.
 * - `location`: Stores the current geolocation coordinates (latitude, longitude).
 * - `openDropOffDialog` and `openPickupDialog`: Boolean values to manage the visibility of confirmation dialogs.
 *
 * The component also integrates functions from the `useCustomer` and `useUI` hooks to handle customer and UI state management.
 *
 * Main Functions:
 * - `handleLocationClick`: Retrieves the user's current geolocation and fetches the corresponding ZIP code.
 * - `handleDropOffSubmit`: Submits the drop-off option with selected dealer details and navigates to the estimate page.
 * - `handleSearchClick`: Initiates a dealer search based on the entered ZIP code and range.
 * - `handleDealerOnClick`: Handles the selection of a dealer for drop-off.
 * - `handleChange`: Updates the customer details form for pickup.
 * - `handlePickupSubmit`: Submits the pickup option with the entered customer details and navigates to the estimate page.
 *
 * Dialogs:
 * - `openDropOffDialog`: Confirms the user's intent to drop off the vehicle at the selected dealer.
 * - `openPickupDialog`: Confirms the user's intent to request a pickup from their location.
 */
function Delivery() {
  const [loading, setLoading] = useState(false);
  const {
    dealerList,
    zip,
    setZip,
    rangeInMiles,
    setRangeInMiles,
    fetchDealersByZip,
    submitPickupDetails,
    customerDetails,
    getZipFromCoords,
    deliveryOption,
    setDeliveryOption
  } = useCustomer();
  const [details, setDetails] = useState(customerDetails);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const navigate = useNavigate();
  const [openDropOffDialog, setOpenDropOffDialog] = useState(false);
  const [openPickupDialog, setOpenPickupDialog] = useState(false);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [addressChanged, setAddressChanged] = useState(false);
  const isMobile = useIsMobile();
  const {showAlert} = useUI();

  useEffect(() => {
    setAddressChanged(
      details?.street1 !== customerDetails?.street1 ||
      details?.street2 !== customerDetails?.street2 ||
      details?.city !== customerDetails?.city ||
      details?.state !== customerDetails?.state ||
      details?.zipCode !== customerDetails?.zipCode
    );
  }, [details, customerDetails]);

  const handleLocationClick = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {

          const {coords} = position;

          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });

          getZipFromCoords(coords);

        },
        (error) => {
          console.error("Error getting location: ", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const handleDropOffSubmit = async () => {
    setLoading(true);
    const {vehicleID} = customerDetails;
    const {addressID, dealerID} = selectedDealer;

    const payload = {
      vehicleID,
      dealerID,
      destinationLocationAddressID: addressID,
      "isDropOff": true
    };

    const response = await submitPickupDetails(payload);
    const {success, message} = response;

    if (success) {
      setLoading(false);
      navigate("/customer/estimate");
      return;
    }
    setLoading(false);
    showAlert(message, "error");
  };

  const handleSearchClick = async (e) => {
    if (zip) {
      setLoading(true);
      await fetchDealersByZip();
      setLoading(false);
    }

    e.preventDefault();
  };

  /**
   *
   * @param dealer
   */
  function handleDealerOnClick(dealer) {
    if (dealer === selectedDealer?.dealerID) {
      setSelectedDealer(null);
      return;
    }
    setSelectedDealer(dealer);
  }

  /**
   * Pickup form
   * @param e
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setDetails({ ...details, [name]: value });
  };

  const handlePickupSubmit = async (e) => {
    e.preventDefault();
    console.log(details);

    const {street1, city, state, zipCode, vehicleID} = details;

    const payload = {
      vehicleID,
      "pickupLocation": {
        "street": street1,
        city,
        state,
        postalCode: zipCode,
        country: "US"
      },
      "isDropOff": false
    };

    if (!addressChanged) {
      payload.pickupLocation.addressID = customerDetails?.addressID;
    }

    const response = await submitPickupDetails(payload);
    const {success, message} = response;
    if (success) {
      navigate("/customer/estimate");
      return;
    }

    showAlert(message, "error");

    console.log(response);
  };

  return (
    <Grid spacing={isMobile ? 0 : 2} container justifyContent="center" alignItems="start" style={{ margin: "25px 0" }}>
      <Paper elevation={3} style={{ padding: "15px", margin: "0 0", maxWidth: "600px", width: "100%" }}>
        <Typography variant="h5" gutterBottom>
          How would you like to return the bike to us?
        </Typography>

        <Lottie width={100} height={100} options={{
          loop: false,
          autoplay: true,
          animationData: map_animation,
          rendererSettings: {
            preserveAspectRatio: "xMidYMid slice"
          }
        }}/>

        <RadioGroup value={deliveryOption}>
          <FormControlLabel
            value="dropOff"
            onClick={() => setDeliveryOption("dropOff")}
            control={<Radio />}
            label="Drop Off"
          />
          {deliveryOption === "dropOff" && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SearchRow>
                    <Typography variant="body1" gutterBottom>
                      Enter your ZIP code to find the nearest authorized Harley-Davidson dealership to schedule your return.
                    </Typography>
                  </SearchRow>
                </Grid>
                <Grid item xs={12} md={4}>
                  <StyleTextField
                    size="small"
                    label="ZIP or Address"
                    variant="outlined"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl>
                    <InputLabel id="select-autowidth-label">Radius</InputLabel>
                    <Select
                      labelId="demo-simple-select-autowidth-label"
                      id="demo-simple-select-autowidth"
                      value={rangeInMiles}
                      size="small"
                      onChange={(val) => setRangeInMiles(val.target.value)}
                      autoWidth
                      label="Radius"
                    >
                      <MenuItem value={25}>
                    25 Miles
                      </MenuItem>
                      <MenuItem value={50}>50 Miles</MenuItem>
                      <MenuItem value={200}>200 Miles</MenuItem>
                      <MenuItem value={500}>500 Miles</MenuItem>
                    </Select>

                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearchClick}>
                    Search
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Link component="button" variant="body2" onClick={handleLocationClick}>
                            Use my location
                  </Link>
                </Grid>
                <Grid item xs={12}>
                  {loading && <StyledBox>
                    <LoadingWrapper>
                      <CircularProgress color="secondary" />
                    </LoadingWrapper>
                  </StyledBox>}

                  {dealerList && dealerList.length < 1 && <Paper style={{ padding: "15px", margin: "0 0" }}>
                    <Alert
                      severity="error"
                      variant="outlined"
                      sx={{ borderRadius: "8px" }} // Adjust if necessary
                    >
                      <Typography>
                  There are no dealers in this area. Try a wider search radius.
                      </Typography>
                    </Alert>
                  </Paper>}

                  {!loading && dealerList !== null && <StyledBox>
                    <List dense>
                      {/* Placeholder dealerships, replace with real data later */}
                      {dealerList.map((item, index) => (
                        <ListItemButton
                          selected={item.dealerID === selectedDealer?.dealerID}
                          key={index}
                          onClick={() => handleDealerOnClick(item)}
                        >
                          <ListItem>
                            <ListItemAvatar>
                              <img src="/assets/Harley_davidson_logo.jpg" alt="Harley Davidson" width="40" />
                            </ListItemAvatar>
                            <ListItemText
                              primary={item.dealer}
                              secondary={<>
                                <Typography
                                  sx={{display: "inline"}}
                                  component="span"
                                  variant="body2"
                                  color="text.primary"
                                >
                                  {`${item.street1}, ${item.city}, ${item.zip}`}
                                </Typography><br/>
                                <Typography
                                  sx={{display: "inline"}}
                                  component="span"
                                  variant="body2"
                                  color="text.primary"
                                >
                                  {`Phone: ${Transform.formatPhoneNumber(item?.phone)}`}
                                </Typography><br/>


                                <Typography
                                  sx={{display: "inline", fontWeight: "bold"}}
                                  component="span"
                                  variant="body2"
                                  color="text.primary"
                                >
                                  {Math.ceil(item.distance)} miles away
                                </Typography>
                              </>}
                            />
                          </ListItem>
                        </ListItemButton>
                      ))}
                    </List>
                  </StyledBox>}
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    style={{ marginTop: "20px" }}
                    onClick={() => setOpenDropOffDialog(true)}
                    disabled={!selectedDealer}
                  >
                Continue
                  </Button>
                </Grid>
              </Grid>
            </>
          )}

          <Typography variant="body2" gutterBottom style={{ marginTop: "20px" }}>
                    Or <Link component="button" variant="body2" onClick={() => setDeliveryOption("pickup")}>have it picked up from your location of choice (additional fees apply).</Link>
          </Typography>
          {deliveryOption === "pickup" && (
            <>
              <Typography variant="body1" gutterBottom sx={{mb: 3}}>
                We can pick up the vehicle from your location of choice for an additional charge of $500 that will be applied to your HDFS account.  Enter your pick-up information below to proceed.
              </Typography>
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Name"
                  variant="outlined"
                  name="customerName"
                  value={details.customerName}
                  onChange={handleChange}
                  style={{ marginBottom: "20px" }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Street Address 1"
                  variant="outlined"
                  name="street1"
                  value={details.street1}
                  onChange={handleChange}
                  style={{ marginBottom: "20px" }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Street Address 2"
                  variant="outlined"
                  name="street2"
                  value={details.street2 || ""}
                  onChange={handleChange}
                  style={{ marginBottom: "20px" }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  variant="outlined"
                  name="city"
                  value={details.city}
                  onChange={handleChange}
                  style={{ marginBottom: "20px" }}
                />
                <FormControl fullWidth variant="outlined" style={{ marginBottom: "20px" }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    size="small"
                    label="State"
                    name="state"
                    value={details.state}
                    onChange={handleChange}
                  >
                    {states.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  label="ZIP Code"
                  variant="outlined"
                  name="zipCode"
                  value={details.zipCode}
                  onChange={handleChange}
                  style={{ marginBottom: "20px" }}
                />
                <Button fullWidth variant="contained" color="primary" onClick={() => setOpenPickupDialog(true)}>
                  Continue
                </Button>
              </>
            </>
          )}
        </RadioGroup>
      </Paper>


      <Dialog
        open={openPickupDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenPickupDialog(false);
          }
        }}
        disableEscapeKeyDown
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Selection"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to proceed? You will be charged an additional $500 transport fee that will be applied to your HDFS account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPickupDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handlePickupSubmit} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDropOffDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenDropOffDialog(false);
          }
        }}
        disableEscapeKeyDown
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Selection"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Contact the dealership to schedule your bike return:<br /><br />
            {selectedDealer?.dealer}, <br />{selectedDealer?.city}, <br />{selectedDealer?.zip},
            <br />{Transform.formatPhoneNumber(selectedDealer?.phone)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setOpenDropOffDialog(false)} color="primary">
            Cancel
          </Button>
          <Button disabled={loading} onClick={handleDropOffSubmit} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default Delivery;
